import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MEAL_PLANNING_CHOOSE_MEALS_PATH, MEAL_PLANNING_PATH, MODAL_ROUTES, RECIPES_PATH } from '@woolworthsnz/shop';
import {
	BreakPointService,
	ButtonComponent,
	Device,
	HistoryService,
	IconCircleComponent,
	RoutedModalBaseComponent,
	SlottedModalComponent,
} from '@woolworthsnz/styleguide';
import { Observable } from 'rxjs';
import { delay, filter, map } from 'rxjs/operators';
import { Stamp } from '../../models/stamp.interface';
import {
	ActiveMealPlanStep,
	MealPlanNonPreferenceStepName,
	MealPlanPreferenceName,
	MealPlanStepName,
} from './choose-meal-plan.model';
import { ChooseMealPlanStore } from './choose-meal-plan.store';
import { LottieModule } from 'ngx-lottie';
import { MealPlanDietaryPreferenceComponent } from '../meal-plan-dietary-preference/meal-plan-dietary-preference.component';
import { MealPlanHouseholdPreferenceComponent } from '../meal-plan-household-preference/meal-plan-household-preference.component';
import { NgIf, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const animationDuration = 6 * 1000;

@Component({
	selector: 'recipe-choose-meal-plan-modal',
	templateUrl: './choose-meal-plan-modal.component.html',
	styleUrls: ['./choose-meal-plan-modal.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [ChooseMealPlanStore],
	standalone: true,
	imports: [
		NgIf,
		SlottedModalComponent,
		IconCircleComponent,
		ButtonComponent,
		NgTemplateOutlet,
		MealPlanHouseholdPreferenceComponent,
		MealPlanDietaryPreferenceComponent,
		LottieModule,
		AsyncPipe,
	],
})
export class ChoosePlanModalComponent extends RoutedModalBaseComponent implements OnInit {
	public activeStep$: Observable<ActiveMealPlanStep> = this.store.activeStep$;
	public isMobileDevice$ = this.breakpointService.device$.pipe(map((device) => device === Device.MOBILE));
	public finalisingAnimationOptions = {
		path: `/recipe/animations/loading.json`,
	};
	public stepName = { ...MealPlanPreferenceName, ...MealPlanNonPreferenceStepName };
	public modalStyle$ = this.activeStep$.pipe(
		map(({ name }: { name: MealPlanStepName }) => {
			switch (name) {
				case this.stepName.splash:
					return 'dark-green';
				case this.stepName.finalising:
					return 'light-green';
				default:
					return 'default';
			}
		})
	);
	isFinalising$ = this.store.isFinalising$.pipe(takeUntilDestroyed());
	private recipeToAdd: Stamp | undefined;

	constructor(
		private readonly store: ChooseMealPlanStore,
		private readonly breakpointService: BreakPointService,
		protected router: Router,
		protected historyService: HistoryService,
		protected activatedRoute: ActivatedRoute
	) {
		super(router, activatedRoute, historyService);
		this.recipeToAdd = this.router.getCurrentNavigation()?.extras?.state?.recipeToAdd as Stamp | undefined;
	}

	public ngOnInit(): void {
		this.redirectAfterFinalising();
	}

	public activateStep(stepName: MealPlanStepName, stepNameToSave?: MealPlanStepName, persist = false): void {
		this.store.activateStep({ stepName, stepNameToSave });

		if (persist) {
			this.store.persistPreferences(this.recipeToAdd);
		}
	}

	public handleClose(): Promise<boolean> {
		const parent = this.activatedRoute.parent;
		if (this.router.url.includes(RECIPES_PATH)) {
			return this.router.navigate([{ outlets: { [MODAL_ROUTES.RECIPE_MODAL]: null } }], { relativeTo: parent });
		}
		return this.router.navigateByUrl(`/${MEAL_PLANNING_PATH}`);
	}

	public saveStepPeople({ value, type }: { value: number; type: 'child' | 'adult' }, step: ActiveMealPlanStep): void {
		const preference = { ...step.default, ...step.value, [type]: value };
		this.store.savePreference({ stepName: step.name, preference });
	}

	public saveDietaryNeedsStep(value: string[]): void {
		this.store.savePreference({ stepName: this.stepName.dietaryNeeds, preference: value });
	}

	private redirectAfterFinalising(): void {
		this.isFinalising$
			.pipe(
				filter((isFinalising) => !!isFinalising),
				delay(animationDuration)
			)
			.subscribe(() => {
				this.router.navigateByUrl(`/${MEAL_PLANNING_PATH}/${MEAL_PLANNING_CHOOSE_MEALS_PATH}`);
			});
	}
}
